import React, { useEffect, useMemo, useRef, useState } from "react";
import { 
  collection, doc, getDocs, query, where, serverTimestamp, writeBatch, limit
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../../../context/AuthContext";
import * as XLSX from "xlsx";

const pad2 = (n) => String(n).padStart(2, "0");
const daysInMonth = (y, m) => new Date(y, m, 0).getDate();
const range = (n) => Array.from({ length: n }, (_, i) => i + 1);
const normalizeVehicleKind = (value) => {
  const cins = String(value || "")
    .toLocaleUpperCase("tr-TR")
    .replace(/\s+/g, "");
  if (cins.includes("POMPA")) return "pompa";
  if (cins.includes("MİKSER") || cins.includes("MIKSER")) return "mikser";
  return "diger";
};

const DURUMLAR = {
  "Ç": { label: "Çalıştı", color: "#dcfce7", textColor: "#166534" },
  "Y": { label: "Yatış", color: "#fef9c3", textColor: "#854d0e" },
  "İ": { label: "İş Verilmedi", color: "#fee2e2", textColor: "#991b1b" },
  "S": { label: "Sanayi (Bakım)", color: "#f1f5f9", textColor: "#475569" },
};

const normalizePuantajDays = (days = {}) => {
  const normalized = {};
  Object.entries(days).forEach(([day, cell]) => {
    normalized[day] = {
      ...cell,
      status: cell?.status === "A" ? "İ" : cell?.status
    };
  });
  return normalized;
};

// Açıklama alanı için optimize edilmiş alt bileşen
const NoteInput = ({ initialValue, onSave, disabled }) => {
  const [val, setVal] = useState(initialValue || "");

  useEffect(() => {
    setVal(initialValue || "");
  }, [initialValue]);

  return (
    <textarea
      disabled={disabled}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => onSave(val)}
      placeholder="..."
      style={{
        width: "100%",
        minHeight: "40px",
        border: "none",
        borderTop: "1px solid #e2e8f0",
        fontSize: "11px",
        textAlign: "left",
        padding: "4px",
        resize: "vertical",
        outline: "none",
        background: "#fff",
        display: "block",
        lineHeight: "1.2",
        fontFamily: "inherit"
      }}
    />
  );
};

const AracPuantajPage = () => {
  const { user, role, userPermissions } = useAuth();
  const [araclar, setAraclar] = useState([]);
  const [puantajMap, setPuantajMap] = useState({});
  const [initialMap, setInitialMap] = useState({}); 
  const [conflictMap, setConflictMap] = useState({}); 
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [allLogs, setAllLogs] = useState({});
  const [checkedDays, setCheckedDays] = useState([]);
  const [vehicleFilterOpen, setVehicleFilterOpen] = useState(false);
  const [vehicleFilterSearch, setVehicleFilterSearch] = useState("");
  const [vehicleFilterValues, setVehicleFilterValues] = useState([]);
  const [vehicleFilterPos, setVehicleFilterPos] = useState({ top: 0, left: 0, width: 260 });
  const vehicleFilterRef = useRef(null);
  const [selectedTesis, setSelectedTesis] = useState("");

  const [autoRange, setAutoRange] = useState(() => {
    const today = new Date();
    const d = today.getDate();
    const m = today.getMonth() + 1;
    const y = today.getFullYear();
    if (d === 1) {
      const prevMonth = m === 1 ? 12 : m - 1;
      const prevYear = m === 1 ? y - 1 : y;
      return { start: `${prevYear}-${pad2(prevMonth)}-01`, end: `${prevYear}-${pad2(prevMonth)}-${pad2(daysInMonth(prevYear, prevMonth))}` };
    } else {
      return { start: `${y}-${pad2(m)}-01`, end: `${y}-${pad2(m)}-${pad2(d - 1)}` };
    }
  });

  const isCreator = (role || "").toLowerCase() === "oluşturucu";
  const perms = userPermissions?.filotakip || {};
  const canSeeLogs = isCreator || perms.yonetici === true;

  useEffect(() => {
    const onDown = (e) => {
      if (vehicleFilterRef.current && !vehicleFilterRef.current.contains(e.target)) {
        setVehicleFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    const fetchAraclar = async () => {
      setLoading(true);
      const q = query(collection(db, "plates"), where("active", "==", true));
      const snap = await getDocs(q);
      let list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(a => ["mikser", "pompa"].includes(normalizeVehicleKind(a.aracCinsi)));

      if (!isCreator && user?.email) {
        try {
          const perSnap = await getDocs(
            query(collection(db, "personeller"), where("email", "==", user.email), limit(1))
          );
          if (!perSnap.empty) {
            const pData = perSnap.docs[0].data();
            const kendi = pData.tesis ? [String(pData.tesis).trim()] : [];
            const ekstra = Array.isArray(pData.gorebilecegiFiloTesisler)
              ? pData.gorebilecegiFiloTesisler.map(t => String(t).trim()) : [];
            const izinli = [...new Set([...kendi, ...ekstra])];
            list = list.filter(a => izinli.includes(String(a.tesis || "").trim()));
          } else {
            list = []; // personeller'de kaydı yok → hiçbir araç göremesin
          }
        } catch (e) { console.error("Tesis filtresi hatası:", e); list = []; }
      }

      setAraclar(list.sort((a, b) => {
        const aKind = normalizeVehicleKind(a.aracCinsi);
        const bKind = normalizeVehicleKind(b.aracCinsi);
        return aKind === bKind ? a.plate.localeCompare(b.plate) : aKind === "pompa" ? -1 : 1;
      }));
      setLoading(false);
    };
    fetchAraclar();
  }, [user, isCreator]);

  useEffect(() => {
    const fetchPuantaj = async () => {
      if (araclar.length === 0) return;
      setLoading(true);
      const ym = `${year}-${pad2(month)}`;
      const q = query(collection(db, "aracPuantaj"), where("yearMonth", "==", ym));
      const snap = await getDocs(q);
      const newMap = {};
      snap.forEach(ds => { newMap[ds.data().plateId] = normalizePuantajDays(ds.data().days || {}); });
      setPuantajMap(newMap);
      setInitialMap(JSON.parse(JSON.stringify(newMap))); 
      setConflictMap({});
      setCheckedDays([]);
      setLoading(false);
    };
    fetchPuantaj();
  }, [araclar, year, month]);

  useEffect(() => {
    const fetchLogs = async () => {
      if (!showLogs || !canSeeLogs) return;
      setLoading(true);
      const ym = `${year}-${pad2(month)}`;
      const q = query(collection(db, "aracPuantajLogsDetails"), where("yearMonth", "==", ym));
      const snap = await getDocs(q);
      const logData = {};
      snap.forEach(d => {
        const data = d.data();
        const key = `${data.plateId}_${data.day}`;
        if (!logData[key]) logData[key] = [];
        logData[key].push(data);
      });
      setAllLogs(logData);
      setLoading(false);
    };
    fetchLogs();
  }, [showLogs, year, month, canSeeLogs]);

  const vehicleOptions = useMemo(() => (
    Array.from(new Set(araclar.map((a) => a.plate).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, "tr"))
  ), [araclar]);

  const normalizedVehicleSearch = useMemo(
    () => vehicleFilterSearch.toUpperCase().replace(/\s+/g, ""),
    [vehicleFilterSearch]
  );

  const visibleVehicleOptions = useMemo(() => (
    vehicleOptions.filter((p) => p.toUpperCase().replace(/\s+/g, "").includes(normalizedVehicleSearch))
  ), [vehicleOptions, normalizedVehicleSearch]);

  const activeVehicleSelection = vehicleFilterValues.length ? vehicleFilterValues : vehicleOptions;

  const vehicleFilteredAraclar = useMemo(() => {
    const allowed = vehicleFilterValues.length ? new Set(vehicleFilterValues) : null;
    return araclar.filter((a) => {
      const plate = String(a.plate || "");
      const matchesSearch = !normalizedVehicleSearch || plate.toUpperCase().replace(/\s+/g, "").includes(normalizedVehicleSearch);
      const matchesSelection = !allowed || allowed.has(plate);
      return matchesSearch && matchesSelection;
    });
  }, [araclar, normalizedVehicleSearch, vehicleFilterValues]);

  const tesisOptions = useMemo(() => (
    Array.from(new Set(vehicleFilteredAraclar.map((a) => String(a.tesis || "").trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, "tr"))
  ), [vehicleFilteredAraclar]);

  useEffect(() => {
    if (selectedTesis && !tesisOptions.includes(selectedTesis)) {
      setSelectedTesis("");
    }
  }, [selectedTesis, tesisOptions]);

  const filteredAraclar = useMemo(() => {
    if (!selectedTesis) return vehicleFilteredAraclar;
    return vehicleFilteredAraclar.filter((a) => String(a.tesis || "").trim() === selectedTesis);
  }, [selectedTesis, vehicleFilteredAraclar]);

  const getVehicleKind = (arac) => {
    return normalizeVehicleKind(arac?.aracCinsi);
  };

  const buildVehicleSummary = (items) => {
    const totalDays = daysInMonth(year, month);
    const totals = {
      vehicles: items.length,
      empty: 0,
      details: { empty: [], "Ç": [], "Y": [], "S": [], "İ": [] },
      "Ç": 0,
      "Y": 0,
      "S": 0,
      "İ": 0
    };
    items.forEach((a) => {
      range(totalDays).forEach((d) => {
        const status = puantajMap[a.id]?.[d]?.status || "";
        const detail = `${a.plate} - ${pad2(d)}.${pad2(month)}.${year}`;
        if (!status) {
          totals.empty += 1;
          totals.details.empty.push(detail);
        } else if (totals[status] !== undefined) {
          totals[status] += 1;
          totals.details[status].push(detail);
        }
      });
    });
    return totals;
  };

  const vehicleSummary = useMemo(() => {
    const all = buildVehicleSummary(filteredAraclar);
    const mikser = buildVehicleSummary(filteredAraclar.filter((a) => getVehicleKind(a) === "mikser"));
    const pompa = buildVehicleSummary(filteredAraclar.filter((a) => getVehicleKind(a) === "pompa"));
    return {
      ...mikser,
      all,
      mikser,
      pompa,
    };
  }, [filteredAraclar, puantajMap, year, month]);

  const legendTitle = (label, details = []) => {
    if (!details.length) return `${label}: 0`;
    return `${label}: ${details.length}\n${details.join("\n")}`;
  };

  const renderSummaryLegend = (label, summary) => (
    <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
      <div title={`${label} Araç: ${summary.vehicles}`} style={{ minWidth: 54, fontSize: "12px", fontWeight: "800", color: "#0f172a", cursor: "help" }}>
        {label}
      </div>
      <div title={`${label} Araç: ${summary.vehicles}`} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: "700", color: "#334155", cursor: "help" }}>
        Araç: {summary.vehicles}
      </div>
      <div style={{ width: 1, alignSelf: "stretch", background: "#cbd5e1" }} />
      <div title={legendTitle("BOŞ", summary.details.empty)} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: "600", cursor: "help" }}>
        <span style={{ background: "#fff", color: "#475569", padding: "2px 8px", borderRadius: "4px", border: "1px solid #ccc" }}>BOŞ</span>
        <span>{summary.empty}</span>
      </div>
      {Object.entries(DURUMLAR).map(([k, v]) => (
        <div key={k} title={legendTitle(v.label, summary.details[k])} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: "600", cursor: "help" }}>
          <span style={{ background: v.color, color: v.textColor, padding: "2px 8px", borderRadius: "4px", border: "1px solid #ccc" }}>{k}</span>
          <span>{v.label}: {summary[k] || 0}</span>
        </div>
      ))}
    </div>
  );

  const openVehicleFilter = (e) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setVehicleFilterPos({ top: r.bottom + 6, left: r.left, width: 280 });
    setVehicleFilterOpen((v) => !v);
  };

  const toggleVehicleOption = (plate) => {
    const current = vehicleFilterValues.length ? vehicleFilterValues : vehicleOptions;
    const next = current.includes(plate) ? current.filter((v) => v !== plate) : [...current, plate];
    setVehicleFilterValues(next.length === vehicleOptions.length ? [] : next);
  };

  const toggleVisibleVehicleOptions = () => {
    const current = vehicleFilterValues.length ? vehicleFilterValues : vehicleOptions;
    const allVisibleSelected = visibleVehicleOptions.length > 0 && visibleVehicleOptions.every((v) => current.includes(v));
    const next = allVisibleSelected
      ? current.filter((v) => !visibleVehicleOptions.includes(v))
      : Array.from(new Set([...current, ...visibleVehicleOptions]));
    setVehicleFilterValues(next.length === vehicleOptions.length ? [] : next);
  };

  const autoFillAction = async () => {
    setLoading(true);
    const newMap = { ...puantajMap };
    const newConflicts = {};
    const daysArr = [];
    try {
      let startD = new Date(autoRange.start + "T12:00:00");
      let endD = new Date(autoRange.end + "T12:00:00");
      for (const arac of araclar) {
        if (!newMap[arac.id]) newMap[arac.id] = {};
        const isMikser = normalizeVehicleKind(arac.aracCinsi) === "mikser";
        const coll = isMikser ? "hazirbeton_mikser_sevkiyat" : "hazirbeton_pompa_hakedis";
        const plateField = isMikser ? "mikserPlaka" : "pompaPlaka";
        const cleanPlate = arac.plate.replace(/\s+/g, '').toUpperCase();
        let curr = new Date(startD);
        while (curr <= endD) {
          if (curr.getMonth() + 1 === month && curr.getFullYear() === year) {
            const dNum = curr.getDate();
            if(!daysArr.includes(dNum)) daysArr.push(dNum);
            const dStr = `${curr.getFullYear()}-${pad2(curr.getMonth()+1)}-${pad2(dNum)}`;
            const q = query(collection(db, coll), where("tarihStr", "==", dStr), where("deleted", "==", false));
            const snap = await getDocs(q);
            const hasActivity = snap.docs.some(d => (d.data()[plateField] || "").replace(/\s+/g, '').toUpperCase() === cleanPlate);
            if (!newMap[arac.id][dNum]?.status) {
              newMap[arac.id][dNum] = { ...newMap[arac.id][dNum], status: hasActivity ? "Ç" : "Y", isAuto: true };
            }
            const currentStatus = newMap[arac.id][dNum].status;
            if ((hasActivity && currentStatus !== "Ç") || (!hasActivity && currentStatus === "Ç")) {
              if(!newConflicts[arac.id]) newConflicts[arac.id] = {};
              newConflicts[arac.id][dNum] = "conflict";
            }
          }
          curr.setDate(curr.getDate() + 1);
        }
      }
      setPuantajMap(newMap);
      setConflictMap(newConflicts);
      setCheckedDays(daysArr);
    } catch (e) { alert(e.message); }
    setLoading(false);
  };

  const handleSave = async () => {
    setLoading(true);
    const batch = writeBatch(db);
    const ym = `${year}-${pad2(month)}`;
    const logRef = collection(db, "aracPuantajLogsDetails");

    try {
      for (const id in puantajMap) {
        const days = puantajMap[id];
        const oldDays = initialMap[id] || {};

        batch.set(doc(db, "aracPuantaj", `${id}_${ym}`), {
          plateId: id, yearMonth: ym, days, updatedAt: serverTimestamp(), updatedBy: user?.email
        }, { merge: true });

        for (const day in days) {
          const newVal = days[day].status;
          const oldVal = oldDays[day]?.status || "";

          if (newVal !== oldVal) {
            const newLogDoc = doc(logRef);
            batch.set(newLogDoc, {
              plateId: id,
              day: Number(day),
              yearMonth: ym,
              oldVal: oldVal || "BOŞ",
              newVal: newVal || "BOŞ",
              user: user?.email || "Bilinmiyor",
              timestamp: serverTimestamp(),
              type: days[day].isAuto ? "OTOMATİK" : "MANUEL"
            });
          }
        }
      }
      await batch.commit();
      setInitialMap(JSON.parse(JSON.stringify(puantajMap))); 
      alert("Başarıyla kaydedildi ve loglar oluşturuldu.");
    } catch (e) { alert(e.message); }
    setLoading(false);
  };

const exportExcel = () => {
  const totalDays = daysInMonth(year, month);
  
  // 1. Başlık satırını oluşturuyoruz
  const headers = [
    "TESİS", "CİNSİ", "PLAKA", "BOŞ GÜN", "ÇALIŞMA", "YATIŞ", "SANAYİ", "İŞ VERİLMEDİ",
    ...range(totalDays)
  ];

  // 2. Veri satırlarını oluşturuyoruz (Her araç için bir dizi)
  const rows = filteredAraclar.map(a => {
    const stats = { "Ç": 0, "Y": 0, "İ": 0, "S": 0, "BOŞ": 0 };
    
    // Önce o araca ait günleri hesaplayıp bir diziye alıyoruz
    const daysArray = range(totalDays).map(d => {
      const status = puantajMap[a.id]?.[d]?.status || "";
      if (status === "") stats["BOŞ"]++;
      else if (stats[status] !== undefined) stats[status]++;
      return status;
    });

    // Satırı tam olarak istediğimiz sırayla diziyoruz
    return [
      a.tesis,
      a.aracCinsi,
      a.plate,
      stats["BOŞ"],
      stats["Ç"],
      stats["Y"],
      stats["S"],
      stats["İ"],
      ...daysArray
    ];
  });

  // 3. Başlıklar ve satırları birleştiriyoruz
  const excelData = [headers, ...rows];

  // 4. Diziler dizisinden (aoa) sayfa oluşturuyoruz
  const ws = XLSX.utils.aoa_to_sheet(excelData);
  
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Puantaj");
  XLSX.writeFile(wb, `Arac_Puantaj_${year}_${month}.xlsx`);
};

  return (
    <div style={{ padding: "20px", backgroundColor: "#f1f5f9", minHeight: "100vh", fontFamily: "sans-serif" }}>
      <style>{`
        .fleet-filter-btn{ width:22px; height:20px; border:1px solid #cbd5e1; border-radius:6px; background:linear-gradient(#fff,#f1f5f9); color:#334155; cursor:pointer; font-size:12px; line-height:16px; padding:0; margin-left:6px; box-shadow:0 1px 1px rgba(15,23,42,.08); }
        .fleet-filter-btn:hover,.fleet-filter-btn.active{ background:#dbeafe; border-color:#60a5fa; color:#1d4ed8; }
        .fleet-filter-menu{ position:fixed; background:#fff; border:1px solid #cbd5e1; border-radius:8px; box-shadow:0 12px 28px rgba(15,23,42,.18); z-index:9999; font-size:12px; padding:10px; max-height:360px; overflow:auto; }
        .fleet-filter-menu input{ width:100%; box-sizing:border-box; border:1px solid #cbd5e1; border-radius:6px; padding:6px; font-size:12px; background:#fff; }
        .fleet-filter-menu button{ border:1px solid #cbd5e1; background:#fff; border-radius:6px; padding:6px; font-size:12px; cursor:pointer; }
        .fleet-filter-menu button:hover{ background:#f1f5f9; }
        .fleet-filter-options{ display:grid; gap:5px; max-height:210px; overflow:auto; border:1px solid #e5e7eb; border-radius:6px; padding:6px; background:#f8fafc; }
      `}</style>
      
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", background: "#fff", padding: "15px", borderRadius: "12px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <button disabled={loading} onClick={() => { setMonth(m => m === 1 ? 12 : m - 1); if(month===1) setYear(year-1); }} style={{ cursor: "pointer", border: "1px solid #ddd", background: "#fff", padding: "5px 12px", borderRadius: "6px" }}>◀</button>
          <select disabled={loading} value={month} onChange={e => setMonth(Number(e.target.value))} style={{ padding: "6px", borderRadius: "6px", fontWeight: "bold", cursor: "pointer" }}>
            {range(12).map(m => <option key={m} value={m}>{new Date(2000, m-1).toLocaleDateString("tr-TR", {month: "long"})}</option>)}
          </select>
          <select disabled={loading} value={year} onChange={e => setYear(Number(e.target.value))} style={{ padding: "6px", borderRadius: "6px", fontWeight: "bold", cursor: "pointer" }}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button disabled={loading} onClick={() => { setMonth(m => m === 12 ? 1 : m + 1); if(month===12) setYear(year+1); }} style={{ cursor: "pointer", border: "1px solid #ddd", background: "#fff", padding: "5px 12px", borderRadius: "6px" }}>▶</button>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "5px", background: "#f8fafc", padding: "5px 10px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
               <input disabled={loading} type="date" value={autoRange.start} onChange={e => setAutoRange({...autoRange, start: e.target.value})} style={{fontSize: "12px", border: "1px solid #cbd5e1", borderRadius: "4px"}} />
               <input disabled={loading} type="date" value={autoRange.end} onChange={e => setAutoRange({...autoRange, end: e.target.value})} style={{fontSize: "12px", border: "1px solid #cbd5e1", borderRadius: "4px"}} />
               <button disabled={loading} onClick={autoFillAction} style={{ background: loading ? "#94a3b8" : "#7c3aed", color: "#fff", border: "none", padding: "6px 12px", borderRadius: "6px", cursor: loading ? "not-allowed" : "pointer", fontWeight: "bold", fontSize: "12px" }}>
                 {loading ? "İŞLENİYOR..." : "HIZLI DOLDUR & KONTROL"}
               </button>
            </div>
            <button disabled={loading} onClick={exportExcel} style={{ background: "#10b981", color: "#fff", border: "none", padding: "8px 15px", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}>EXCEL İNDİR</button>
            <button disabled={loading} onClick={handleSave} style={{ background: "#2563eb", color: "#fff", border: "none", padding: "8px 25px", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}>{loading ? "BEKLEYİN..." : "KAYDET"}</button>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "15px", alignItems: "center" }}>
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <select
            disabled={loading}
            value={selectedTesis}
            onChange={(e) => setSelectedTesis(e.target.value)}
            style={{ padding: "5px 8px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12px", background: "#fff", fontWeight: 700 }}
            title="Listelenen araçların tesisleri arasından seç"
          >
            <option value="">Tüm Tesisler</option>
            {tesisOptions.map((tesis) => (
              <option key={tesis} value={tesis}>{tesis}</option>
            ))}
          </select>
          <div style={{ width: "100%", marginTop: "4px" }}>
            {renderSummaryLegend("Mikser", vehicleSummary.mikser)}
          </div>
          <div style={{ width: "100%", marginTop: "4px" }}>
            {renderSummaryLegend("Pompa", vehicleSummary.pompa)}
          </div>
        </div>
        <div style={{ display: "flex", gap: "15px" }}>
          <label style={{ cursor: "pointer", fontSize: "13px" }}><input type="checkbox" checked={showNotes} onChange={e => setShowNotes(e.target.checked)} /> Açıklamalar</label>
          {canSeeLogs && <label style={{ cursor: "pointer", fontSize: "13px" }}><input type="checkbox" checked={showLogs} onChange={e => setShowLogs(e.target.checked)} /> Loglar</label>}
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", overflowY: "auto", maxHeight: "calc(100vh - 200px)", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: "12px" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 20, background: "#f8fafc" }}>
            <tr>
              <th style={{ width: "180px", padding: "12px", border: "1px solid #e2e8f0", position: "sticky", left: 0, background: "#f8fafc", zIndex: 30 }}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  ARAÇ LİSTESİ
                  <button type="button" className={`fleet-filter-btn ${vehicleFilterValues.length || vehicleFilterSearch ? "active" : ""}`} onClick={openVehicleFilter} title="Araç filtrele">▾</button>
                </span>
              </th>
              {range(daysInMonth(year, month)).map(d => (
                <th key={d} style={{ width: "45px", border: "1px solid #e2e8f0", position: "relative" }}>
                  {checkedDays.includes(d) && <span style={{ position: "absolute", top: 2, right: 2, width: 6, height: 6, background: "#3b82f6", borderRadius: "50%" }}></span>}
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAraclar.map((arac) => {
              const isPompa = normalizeVehicleKind(arac.aracCinsi) === "pompa";
              return (
                <tr key={arac.id}>
                  <td style={{ padding: "10px", border: "1px solid #e2e8f0", fontWeight: "bold", position: "sticky", left: 0, background: isPompa ? "#f0fdf4" : "#fdf2f8", zIndex: 5, color: isPompa ? "#166534" : "#9d174d" }}>
                    {arac.plate}
                  </td>
                  {range(daysInMonth(year, month)).map(day => {
                    const cell = puantajMap[arac.id]?.[day] || { status: "", note: "", isAuto: false };
                    const isConflict = conflictMap[arac.id]?.[day] === "conflict";
                    const logs = allLogs[`${arac.id}_${day}`] || [];
                    const hasNote = cell.note && cell.note.trim() !== "";

                    let popUp = cell.note ? `NOT: ${cell.note}` : "";
                    if (showLogs && logs.length > 0) {
                      const logStr = logs.map(l => {
                        const d = l.timestamp?.toDate ? l.timestamp.toDate() : new Date();
                        const formattedDate = `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
                        const formattedTime = d.toLocaleTimeString("tr-TR", {hour:"2-digit", minute:"2-digit"});
                        const type = l.type === "OTOMATİK" ? " [OTOMATİK]" : " [MANUEL]";
                        return `[${formattedDate} ${formattedTime}] ${l.user.split('@')[0]}: ${l.oldVal}->${l.newVal}${type}`;
                      }).join("\n");
                      popUp = popUp ? `${popUp}\n-----------\n${logStr}` : logStr;
                    }

                    let bg = DURUMLAR[cell.status]?.color || "transparent";
                    let style = { width: "100%", height: "35px", border: "none", background: bg, textAlign: "center", fontWeight: "bold", cursor: "pointer", appearance: "none" };
                    if(isConflict) {
                      style.backgroundImage = "repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.4) 5px, rgba(255,255,255,0.4) 10px)";
                      style.border = "2px solid red";
                    }

                    return (
                      <td key={day} title={popUp} style={{ border: "1px solid #e2e8f0", padding: 0, position: "relative" }}>
                        {hasNote && <span style={{ position: "absolute", top: "2px", right: "2px", width: "6px", height: "6px", background: "red", borderRadius: "50%", zIndex: 10, pointerEvents: "none" }}></span>}
                        {isConflict && <span style={{ position: "absolute", top: 0, right: 0, width: 0, height: 0, borderStyle: "solid", borderWidth: "0 8px 8px 0", borderColor: "transparent red transparent transparent", zIndex: 2 }}></span>}
                        <select 
                          disabled={loading} value={cell.status} 
                          onChange={e => setPuantajMap({...puantajMap, [arac.id]: {...puantajMap[arac.id], [day]: {...cell, status: e.target.value, isAuto: false}}})}
                          style={style}
                        >
                          <option value=""></option>
                          {Object.keys(DURUMLAR).map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                        {showNotes && (
                          <NoteInput 
                            disabled={loading}
                            initialValue={cell.note}
                            onSave={(newNote) => setPuantajMap({...puantajMap, [arac.id]: {...puantajMap[arac.id], [day]: {...cell, note: newNote}}})}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {vehicleFilterOpen && (
        <div ref={vehicleFilterRef} className="fleet-filter-menu" style={{ top: vehicleFilterPos.top, left: vehicleFilterPos.left, width: vehicleFilterPos.width }}>
          <div style={{ fontWeight: 700, marginBottom: 8, color: "#0f172a" }}>Araç Listesi</div>
          <input
            value={vehicleFilterSearch}
            onChange={(e) => setVehicleFilterSearch(e.target.value)}
            placeholder="Plaka ara..."
          />
          <label style={{ display: "flex", alignItems: "center", gap: 6, margin: "8px 0", paddingBottom: 6, borderBottom: "1px solid #e5e7eb", cursor: "pointer", fontWeight: 700 }}>
            <input
              type="checkbox"
              checked={visibleVehicleOptions.length > 0 && visibleVehicleOptions.every((p) => activeVehicleSelection.includes(p))}
              onChange={toggleVisibleVehicleOptions}
              style={{ width: "auto" }}
            />
            <span>Tümünü Seç / Kaldır</span>
          </label>
          <div className="fleet-filter-options">
            {visibleVehicleOptions.map((plate) => (
              <label key={plate} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={activeVehicleSelection.includes(plate)}
                  onChange={() => toggleVehicleOption(plate)}
                  style={{ width: "auto" }}
                />
                <span>{plate}</span>
              </label>
            ))}
          </div>
          <button onClick={() => { setVehicleFilterValues([]); setVehicleFilterSearch(""); }} style={{ width: "100%", marginTop: 8 }}>Temizle</button>
        </div>
      )}
    </div>
  );
};

export default AracPuantajPage;
